import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { ModelArtifactType, TreeLeaf, TreeType, TreeViewBy } from '../types';
import { TREE_NODE, TREE_TYPE, TREE_VIEW_BY } from '../constants/urlParams';
import { TreeViewNode } from '../components/TreeView/TreeView';
import * as atoms from '../atoms/atoms';
import useSearchParams from './useSearchParams';
import useTreeViewParams from './useTreeViewParams';

type TreeItemUrlParam = {
  id: string;
  parentId?: string;
  columnId?: string;
  columnType?: ModelArtifactType;
} | null;

export type TreeNodeRef = {
  id: string;
  resourceType?: TreeLeaf['resource_type'] | 'folder';
};

const useSelectedTreeItem = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { treeViewBy } = useTreeViewParams();
  const currentTreeData = useRecoilValue(atoms.currentTreeData);
  const currentTreeNodesById = useRecoilValue(atoms.currentTreeNodesById);

  const searchParam: TreeItemUrlParam = JSON.parse(
    searchParams.get(TREE_NODE) || 'null',
  );

  const selectedTagOrOwner: string = useMemo(() => {
    if (
      searchParams.get(TREE_NODE) &&
      (treeViewBy === 'tags' || treeViewBy === 'owners')
    ) {
      const treeNode = JSON.parse(
        searchParams.get(TREE_NODE)?.replace('folderNode_', '') || 'null',
      );

      return (
        treeNode?.parentId ||
        treeNode?.id ||
        (treeViewBy === 'tags' ? 'No tags' : 'No owners')
      );
    }

    return '';
  }, [treeViewBy, searchParams]);

  const formatUrlParam = (
    modelId?: string,
    columnId?: string,
    columnType?: ModelArtifactType,
  ) => {
    if (!modelId) return undefined;

    const treeNode = currentTreeNodesById[modelId];
    return JSON.stringify({
      id: modelId,
      parentId: treeNode?.parentId,
      columnId,
      columnType,
    });
  };

  const setSelectedTreeModel = (
    modelId?: string,
    columnId?: string,
    treeType?: TreeType,
    treeViewBy?: TreeViewBy,
    columnType?: ModelArtifactType,
  ) => {
    const urlParam = formatUrlParam(modelId, columnId, columnType);

    if (!urlParam) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete(TREE_NODE);
      setSearchParams(newParams);
      return;
    }

    setSearchParams((prev) => {
      prev.set(TREE_NODE, urlParam);
      if (treeType) {
        prev.set(TREE_TYPE, treeType);
      }
      if (treeViewBy) {
        prev.set(TREE_VIEW_BY, treeViewBy);
      }
      return prev;
    });
  };

  const selectedTreeFolderNodes = useMemo((): TreeNodeRef[] | undefined => {
    if (!currentTreeData) return [];

    const selectedNodeId = searchParam?.id;
    let nodesToSearch: TreeViewNode[] = [];
    if (selectedNodeId) {
      const node = currentTreeNodesById[selectedNodeId];
      if (!node || node.resourceType !== 'folder') return undefined;
      nodesToSearch = [node];
    } else {
      nodesToSearch = [...currentTreeData];
    }

    const nodes: TreeNodeRef[] = [];
    let currentSearchedNode = nodesToSearch.shift();
    while (currentSearchedNode) {
      const children = currentSearchedNode.children || [];
      if (children.length > 0) {
        nodesToSearch.push(...children);
      }
      const isNodeFolder = currentSearchedNode.resourceType === 'folder';
      if (!isNodeFolder) {
        nodes.push({
          id: currentSearchedNode.id,
          resourceType: currentSearchedNode.resourceType,
        });
      }
      currentSearchedNode = nodesToSearch.shift();
    }
    return nodes;
  }, [currentTreeData, currentTreeNodesById, searchParam?.id]);

  const selectedTreeNode = useMemo((): TreeNodeRef | undefined => {
    if (!searchParam?.id) return undefined;
    const node = currentTreeNodesById[searchParam.id];
    if (!node) return undefined;
    return {
      id: node.id,
      resourceType: node.resourceType,
    };
  }, [currentTreeNodesById, searchParam?.id]);

  return {
    selectedTagOrOwner,
    formatUrlParam,
    setSelectedTreeModel,
    selectedModelColumn: searchParam?.columnId,
    selectedModelColumnType: searchParam?.columnType,
    selectedTreeFolderNodes,
    selectedTreeNode,
  };
};

export default useSelectedTreeItem;
